import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { isIOS, isStandalone, iosSupportsPush } from '../lib/platform';
import { ensurePushSubscribed } from '../hooks/usePushRegistration';
import { Share, X, BellRing, Download } from 'lucide-react';

const DISMISS_KEY = 'tababa_install_dismissed';
const HIDDEN_PATHS = ['/doctor/call/', '/psychology/join-call/', '/call/', '/permissions', '/install'];

const dismissed = () => {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};

/**
 * Install / notification nudge.
 *  - iOS Safari      → "Add to Home Screen" steps (Apple blocks push in tabs)
 *  - installed iOS   → one-tap Enable notifications (needs a user gesture)
 *  - Android/Chrome  → native one-tap install via `beforeinstallprompt`
 */
export const InstallPrompt = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [mode, setMode] = useState(null); // 'ios-install' | 'ios-enable' | 'android'
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (isIOS()) {
      if (!iosSupportsPush()) return undefined;
      if (isStandalone()) {
        if ('Notification' in window && Notification.permission === 'default') setMode('ios-enable');
        return undefined;
      }
      if (!dismissed()) setMode('ios-install');
      return undefined;
    }

    // Android / desktop Chrome: the browser tells us when it can install.
    const onPrompt = (e) => {
      e.preventDefault();
      if (isStandalone() || dismissed()) return;
      setDeferred(e);
      setMode('android');
    };
    const onInstalled = () => setMode(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    setMode(null);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  const enableNotifications = async () => {
    try {
      const res = await Notification.requestPermission();
      if (res === 'granted') await ensurePushSubscribed();
    } catch { /* ignore */ }
    setMode(null);
  };

  const installAndroid = async () => {
    try {
      await deferred?.prompt();
      await deferred?.userChoice;
    } catch { /* ignore */ }
    setMode(null);
  };

  if (!mode || HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  const guideLink = (
    <a href="/install" className="underline font-semibold" data-testid="install-prompt-guide-link">
      {t('طريقة التثبيت', 'How to install')}
    </a>
  );

  return (
    <div
      className={`fixed bottom-4 inset-x-3 z-[900] rounded-2xl bg-[#0d47a1] text-white shadow-2xl px-4 py-3 flex items-start gap-3 ${language === 'ar' ? 'rtl' : 'ltr'}`}
      data-testid={`install-prompt-${mode}`}
    >
      <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
        {mode === 'android' ? <Download size={17} /> : <BellRing size={17} />}
      </span>

      <div className="flex-1 min-w-0 text-start">
        {mode === 'ios-enable' && (
          <>
            <p className="text-sm font-bold">{t('فعّل الإشعارات', 'Turn on notifications')}</p>
            <p className="text-[12px] text-blue-100 mt-0.5 leading-snug">
              {t('لتصلك المكالمات والمواعيد حتى لو كان الهاتف مقفلاً.',
                 'So calls and appointments reach you even when your phone is locked.')}
            </p>
            <button
              onClick={enableNotifications}
              className="mt-2 rounded-full bg-white text-[#0d47a1] px-4 py-1.5 text-xs font-bold"
              data-testid="ios-enable-notifications-btn"
            >
              {t('تفعيل الآن', 'Enable now')}
            </button>
          </>
        )}

        {mode === 'ios-install' && (
          <>
            <p className="text-sm font-bold">
              {t('ثبّت تطبيق Tababa.co لتلقّي الإشعارات', 'Install Tababa.co to get notifications')}
            </p>
            <p className="text-[12px] text-blue-100 mt-0.5 leading-snug">
              {t('على iPhone: اضغط زر المشاركة', 'On iPhone: tap the Share button')}
              <Share size={12} className="inline mx-1 -mt-0.5" />
              {t('ثم «إضافة إلى الشاشة الرئيسية». ', 'then "Add to Home Screen". ')}
              {guideLink}
            </p>
          </>
        )}

        {mode === 'android' && (
          <>
            <p className="text-sm font-bold">
              {t('ثبّت تطبيق Tababa.co على هاتفك', 'Install the Tababa.co app')}
            </p>
            <p className="text-[12px] text-blue-100 mt-0.5 leading-snug">
              {t('فتح أسرع وإشعارات فورية للمكالمات والمواعيد. ',
                 'Faster access and instant alerts for calls and appointments. ')}
              {guideLink}
            </p>
            <button
              onClick={installAndroid}
              className="mt-2 rounded-full bg-white text-[#0d47a1] px-4 py-1.5 text-xs font-bold"
              data-testid="android-install-btn"
            >
              {t('تثبيت التطبيق', 'Install app')}
            </button>
          </>
        )}
      </div>

      <button
        onClick={dismiss}
        className="p-1 rounded-full hover:bg-white/15 flex-shrink-0"
        aria-label="dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default InstallPrompt;
